import { ref } from '@nuxtjs/composition-api';

export interface IBikeFinderMake {
  slug: string;
  title: string;
  logoSrc?: string;
}

// Module-level so every make card and the single layout-mounted modal share
// one state. Only ever set from client-side clicks.
const isOpen = ref(false);
const make = ref<IBikeFinderMake | null>(null);

export const isPlainLeftClick = (event: MouseEvent): boolean =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

export const getMakeFromElement = (target: EventTarget | null): IBikeFinderMake | null => {
  const card = (target as HTMLElement | null)?.closest?.('[data-make-slug]') as HTMLElement | null;
  const slug = card?.dataset.makeSlug;
  if (!card || !slug) return null;

  return {
    slug,
    title: card.dataset.makeTitle || slug,
    ...(card.dataset.makeLogo && { logoSrc: card.dataset.makeLogo }),
  };
};

const useBikeFinderModal = () => {
  const open = (nextMake: IBikeFinderMake) => {
    make.value = nextMake;
    isOpen.value = true;
  };

  const close = () => {
    isOpen.value = false;
  };

  /**
   * Bind with `.capture` on an ancestor of the make links: router-link only
   * skips navigation if `defaultPrevented` is already set when its own click
   * handler runs. Modified clicks (new tab etc.) fall through to the link.
   */
  const interceptMakeClick = (event: MouseEvent) => {
    if (!isPlainLeftClick(event)) return;
    const clickedMake = getMakeFromElement(event.target);
    if (!clickedMake) return;

    event.preventDefault();
    open(clickedMake);
  };

  return { isOpen, make, open, close, interceptMakeClick };
};

export default useBikeFinderModal;
